import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Radio,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Icon } from '@iconify/react/dist/iconify.js';

import {
  clearDraftValidation,
  saveQuestion,
  validateQuestionDraft,
} from '../../../redux/features/assessmentCmsSlice';
import ImageField from './ImageField';
import { LADDER_TRACKS, TRACK_LABELS } from './tracks';

/**
 * The question editor.
 *
 * ---------------------------------------------------------------------------
 * THE DESIGN PROBLEM
 *
 * These questions used to be written by developers, in code, where a reviewer
 * read the diff. A chief mentor working in a form has no reviewer, and the ways
 * to break a question are almost all INVISIBLE — the question still renders, it
 * just becomes unanswerable, or scores backwards. So the form's job is not to
 * collect fields. It is to make the dangerous shapes hard to express:
 *
 *   * Answers are edited BESIDE the blank they grade, never as a separate
 *     list. The parallel-arrays contract cannot be violated by typing, because
 *     adding a blank adds its answer box with it.
 *
 *   * The KG correct answer is a RADIO BUTTON on the option list, not a text
 *     box. KG grades by exact string match against the option label, so a typed
 *     answer that differs by one space is an unwinnable question. Picking it
 *     from the options makes that impossible rather than merely validated.
 *
 *   * Parent scores are filled in DESCENDING by default and the form offers to
 *     re-apply that order. Getting this backwards is the inverted-polarity bug
 *     that already shipped once, where a struggling child read as thriving.
 *
 *   * What the grader will accept is shown back, live, under each answer. The
 *     spaced-slash rule ("Pin / Pen" means either, "1/2" means one fraction) is
 *     genuinely subtle, and showing the result beats explaining the rule.
 *
 * The server validates all of this again — this form is the explanation, not
 * the enforcement.
 */

// LADDER_TRACKS and TRACK_LABELS come from ./tracks — see that file for why
// they are not defined here.

const LADDER_TYPES = [
  { value: 'fill_blanks', label: 'Fill in the blanks' },
  { value: 'multiple_choice', label: 'Multiple choice' },
  { value: 'identify', label: 'Identify' },
  { value: 'match', label: 'Match the following' },
  { value: 'descriptive', label: 'Descriptive' },
];

const KG_BANDS = [
  { value: 'basic', label: 'Basic — recognition' },
  { value: 'standard', label: 'Standard — recall with reasoning' },
  { value: 'advanced', label: 'Advanced — application and logic' },
];

const emptyDraft = (track) => ({
  text: '',
  type: track === 'kg' ? 'picture_choice' : track === 'parent' ? 'profile_choice' : 'fill_blanks',
  subItems: [],
  correctAnswers: [''],
  matchMode: null,
  optionsLayout: null,
  options: [],
  marks: track === 'parent' ? 0 : track === 'kg' ? 1 : 2,
  promptMedia: null,
  band: track === 'kg' ? 'basic' : null,
  isScored: track !== 'parent' ? true : true,
  dimension: '',
  isEnabled: true,
});

/** Server shape -> form shape. Options arrive grouped; keep them that way. */
const toDraft = (question, track) => {
  if (!question) return emptyDraft(track);
  return {
    text: question.text || '',
    type: question.type,
    subItems: question.subItems || [],
    correctAnswers: question.correctAnswers?.length ? question.correctAnswers : [''],
    matchMode: question.matchMode || null,
    optionsLayout: question.optionsLayout || null,
    options: question.options || [],
    marks: question.marks ?? 2,
    promptMedia: question.promptMedia || null,
    band: question.band || null,
    isScored: question.isScored !== false,
    dimension: question.dimension || '',
    isEnabled: question.isEnabled !== false,
  };
};

const QuestionEditorDialog = ({ open, onClose, onSaved, question, level, subject }) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { draft: validation, saving } = useSelector((state) => state.assessmentCms);

  const track = subject?.track || 'foundation';
  // foundation, secondary and higher_secondary author identically. Gating on
  // the exact string 'foundation' is what hid every field on the two newer
  // papers — see the note above LADDER_TRACKS.
  const isLadder = LADDER_TRACKS.includes(track);
  const isEdit = Boolean(question?.id);

  const [form, setForm] = useState(() => toDraft(question, track));

  useEffect(() => {
    if (open) {
      setForm(toDraft(question, track));
      dispatch(clearDraftValidation());
    }
  }, [open, question, track, dispatch]);

  const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  /* ---------------------------------------------------------------- *
   * Live validation
   *
   * Debounced against the form rather than fired on blur, so the accepted-
   * answers preview keeps up with typing. It is a dry run — nothing is
   * written — and its result is advisory until Save.
   * ---------------------------------------------------------------- */
  useEffect(() => {
    if (!open || !level?.id) return undefined;
    const timer = setTimeout(() => {
      dispatch(validateQuestionDraft({ levelId: level.id, ...form }));
    }, 400);
    return () => clearTimeout(timer);
  }, [open, level?.id, form, dispatch]);

  /* ---------------------------------------------------------------- *
   * Blanks and answers — always added and removed together
   * ---------------------------------------------------------------- */

  /**
   * Repair a draft whose answers do not line up with its blanks.
   *
   * The call sites below keep them in step, so this should never fire. It is
   * here because when they DID fall out of step the result was a dead end: the
   * extra answer had no field, the server refused the save, and no amount of
   * editing could reach the value causing it.
   *
   * A mismatch is now corrected on sight rather than presented as an error the
   * author cannot act on. It converges in one pass — `answersForBlanks` is
   * idempotent — and the length guard is what stops it re-entering.
   */
  useEffect(() => {
    if (!open) return;
    const wanted = form.subItems.length || 1;
    if (form.correctAnswers.length === wanted) return;
    setForm((prev) => ({
      ...prev,
      correctAnswers: answersForBlanks(prev.correctAnswers, prev.subItems.length),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, form.subItems.length, form.correctAnswers.length]);

  /**
   * `correctAnswers` sized to match the blanks, keeping what is already typed.
   *
   * ---------------------------------------------------------------------------
   * THE OFF-BY-ONE THIS EXISTS TO KILL
   *
   * A fresh draft has NO blanks and ONE answer — that is the "no separate
   * blanks, one answer grades the whole question" case, and it is correct.
   * `addBlank` then appended to both arrays, so the first blank produced
   * 1 blank / 2 answers and the second 2 blanks / 3 answers.
   *
   * The form renders one answer field PER BLANK, so answer 3 had no field. The
   * server refused the save with "2 blank(s) but 3 answer(s)" and "Answer 3 is
   * empty", and there was no box on screen to put an answer in — the Add
   * Question button stayed disabled with no way to earn it back. Exactly the
   * failure section 12.8 described, in a different form: a value that decides
   * whether you can save, which you cannot see or reach.
   *
   * The first blank must ADOPT the answer that is already there rather than
   * push it out of view. So sizing is derived from the blank count instead of
   * being maintained by hand at each call site:
   *
   *   0 blanks -> exactly one answer, for the question as a whole
   *   n blanks -> exactly n, answer i grading blank i
   */
  const answersForBlanks = (answers, blankCount) => {
    const kept = Array.isArray(answers) ? answers : [];
    if (blankCount === 0) return [kept[0] ?? ''];
    const next = kept.slice(0, blankCount);
    while (next.length < blankCount) next.push('');
    return next;
  };

  const addBlank = () => {
    const subItems = [...form.subItems, ''];
    set({
      subItems,
      correctAnswers: answersForBlanks(form.correctAnswers, subItems.length),
      // A nested option list is indexed by blank, so a new blank needs its own
      // empty group or the arrays stop lining up.
      options:
        form.optionsLayout === 'nested' ? [...form.options, []] : form.options,
    });
  };

  const removeBlank = (index) => {
    const subItems = form.subItems.filter((_, i) => i !== index);
    set({
      subItems,
      // Drop that blank's own answer first, THEN size to what is left. Sizing
      // alone would keep the leading answers and silently shift every one of
      // them up a blank.
      correctAnswers: answersForBlanks(
        form.correctAnswers.filter((_, i) => i !== index),
        subItems.length,
      ),
      options:
        form.optionsLayout === 'nested'
          ? form.options.filter((_, i) => i !== index)
          : form.options,
    });
  };

  const setBlank = (index, value) =>
    set({ subItems: form.subItems.map((item, i) => (i === index ? value : item)) });

  const setAnswer = (index, value) =>
    set({
      correctAnswers: form.correctAnswers.map((item, i) => (i === index ? value : item)),
    });

  /* ---------------------------------------------------------------- *
   * Options
   * ---------------------------------------------------------------- */

  const optionsLayout = form.optionsLayout;

  const setFlatOptions = (options) => set({ options, optionsLayout: options.length ? 'flat' : null });

  const addFlatOption = () => {
    if (isLadder) return setFlatOptions([...form.options, '']);
    // KG and parent options are objects. A new parent option continues the
    // descending scale rather than starting at 0, so the common case needs no
    // correction.
    const next =
      track === 'parent'
        ? { label: '', score: form.isScored ? Math.max(1, form.options.length ? nextScore(form.options) : 4) : null }
        : { label: '', image: '', imageKey: null };
    return setFlatOptions([...form.options, next]);
  };

  const nextScore = (options) => {
    const lowest = options
      .map((o) => Number(o.score))
      .filter((n) => Number.isFinite(n))
      .reduce((min, n) => Math.min(min, n), Infinity);
    return Number.isFinite(lowest) ? lowest - 1 : 4;
  };

  const setOption = (index, patch) =>
    setFlatOptions(
      form.options.map((option, i) => {
        if (i !== index) return option;
        if (typeof option === 'string') return patch.label ?? option;
        return { ...option, ...patch };
      })
    );

  const removeOption = (index) => setFlatOptions(form.options.filter((_, i) => i !== index));

  /** Re-apply the descending scale, highest first. */
  const applyDescendingScores = () =>
    setFlatOptions(
      form.options.map((option, index) => ({
        ...option,
        score: Math.max(1, form.options.length - index),
      }))
    );

  const setNestedOption = (groupIndex, optionIndex, value) =>
    set({
      options: form.options.map((group, gi) =>
        gi === groupIndex ? group.map((o, oi) => (oi === optionIndex ? value : o)) : group
      ),
    });

  const addNestedOption = (groupIndex) =>
    set({
      options: form.options.map((group, gi) => (gi === groupIndex ? [...group, ''] : group)),
    });

  const removeNestedOption = (groupIndex, optionIndex) =>
    set({
      options: form.options.map((group, gi) =>
        gi === groupIndex ? group.filter((_, oi) => oi !== optionIndex) : group
      ),
    });

  /** Flat <-> nested. Nested needs one group per blank, so seed them. */
  const switchLayout = (layout) => {
    if (layout === 'nested') {
      const groups = (form.subItems.length ? form.subItems : ['']).map((_, i) =>
        Array.isArray(form.options[i]) ? form.options[i] : []
      );
      set({ optionsLayout: 'nested', options: groups });
    } else if (layout === 'flat') {
      const flat = form.options.flat().map((o) => (typeof o === 'string' ? o : o.label));
      set({ optionsLayout: 'flat', options: flat });
    } else {
      set({ optionsLayout: null, options: [] });
    }
  };

  /* ---------------------------------------------------------------- *
   * Save
   * ---------------------------------------------------------------- */

  const handleSave = async () => {
    try {
      const payload = {
        id: question?.id,
        levelId: level.id,
        ...form,
        // The server treats an empty dimension as absent; sending "" would
        // store an empty string that reads as a real, blank dimension.
        dimension: form.dimension || null,
        marks: Number(form.marks) || 0,
      };
      const res = await dispatch(saveQuestion(payload)).unwrap();
      const warnings = res?.data?.warnings || [];
      enqueueSnackbar(isEdit ? 'Question updated' : 'Question added', { variant: 'success' });
      warnings.forEach((w) => enqueueSnackbar(w, { variant: 'warning', autoHideDuration: 8000 }));
      onSaved?.();
      onClose();
    } catch (err) {
      // The server's messages ARE the guidance — show the first one rather
      // than a generic failure.
      enqueueSnackbar(err?.message || 'Could not save the question', { variant: 'error' });
    }
  };

  const accepted = validation.accepted || [];
  const blockingErrors = validation.errors || [];

  const totalIfSaved = useMemo(() => Number(form.marks) || 0, [form.marks]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography variant="h6">
              {isEdit ? `Edit question ${question.questionOrder}` : 'Add a question'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {subject?.label}
              {level?.category ? ` · Category ${level.category}` : ''} · {level?.displayName}
            </Typography>
          </Box>
          <Chip
            size="small"
            // Never a catch-all. The old fallback said "Foundation" for every
            // track it did not name, which told a Class 9 editor they were
            // editing the Class 1-4 paper.
            label={TRACK_LABELS[track] || track}
            color={isLadder ? 'primary' : 'default'}
            variant="outlined"
          />
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2.5}>
          {blockingErrors.length > 0 && (
            <Alert severity="error">
              <AlertTitle>Fix before saving</AlertTitle>
              <Stack component="ul" sx={{ pl: 2, m: 0 }} spacing={0.5}>
                {blockingErrors.map((message) => (
                  <li key={message}>
                    <Typography variant="body2">{message}</Typography>
                  </li>
                ))}
              </Stack>
            </Alert>
          )}

          {validation.warnings?.length > 0 && (
            <Alert severity="warning">
              <AlertTitle>Worth a second look</AlertTitle>
              <Stack component="ul" sx={{ pl: 2, m: 0 }} spacing={0.5}>
                {validation.warnings.map((message) => (
                  <li key={message}>
                    <Typography variant="body2">{message}</Typography>
                  </li>
                ))}
              </Stack>
              <Typography variant="caption" color="text.secondary">
                These do not block saving.
              </Typography>
            </Alert>
          )}

          {/* ---------- the question itself ---------- */}
          <TextField
            label="Question"
            fullWidth
            multiline
            minRows={2}
            value={form.text}
            onChange={(e) => set({ text: e.target.value })}
            placeholder={
              track === 'kg'
                ? 'Identify the animal'
                : track === 'parent'
                ? "How would you rate your child's current academic performance?"
                : 'Fill in the blanks with the correct blend:'
            }
          />

          <Grid container spacing={2}>
            {isLadder && (
              <>
                <Grid item xs={12} sm={4}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="Type"
                    value={form.type}
                    onChange={(e) => set({ type: e.target.value })}
                  >
                    {LADDER_TYPES.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label="Marks"
                    value={form.marks}
                    onChange={(e) => set({ marks: e.target.value })}
                    helperText={`Level total would be ${(level?.totalMarks || 0) - (question?.marks || 0) + totalIfSaved}`}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={form.matchMode === 'keywords'}
                        onChange={(e) => set({ matchMode: e.target.checked ? 'keywords' : null })}
                      />
                    }
                    label="Grade by keywords"
                  />
                  <Tooltip
                    title={
                      'For descriptive answers. The answer becomes a spec: commas separate ' +
                      'required ideas, slashes list acceptable wordings for each. ' +
                      '"flat/2d, solid/3d" needs a flat-word AND a solid-word.'
                    }
                  >
                    <Icon icon="mdi:information-outline" width={16} />
                  </Tooltip>
                </Grid>
              </>
            )}

            {track === 'kg' && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="Band"
                    value={form.band || ''}
                    onChange={(e) => set({ band: e.target.value })}
                    helperText="Groups the report. It never gates progression."
                  >
                    {KG_BANDS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label="Marks"
                    value={form.marks}
                    onChange={(e) => set({ marks: e.target.value })}
                  />
                </Grid>
              </>
            )}

            {track === 'parent' && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Dimension"
                    value={form.dimension}
                    onChange={(e) => set({ dimension: e.target.value })}
                    placeholder="Focus Span"
                    helperText="How the sales team groups this answer."
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={form.isScored}
                        onChange={(e) => {
                          const isScored = e.target.checked;
                          set({
                            isScored,
                            // Flipping to profiling has to clear the scores, or
                            // the question is half-scored and nothing
                            // downstream knows what to do with it.
                            options: form.options.map((option, index) => ({
                              ...option,
                              score: isScored ? Math.max(1, form.options.length - index) : null,
                            })),
                          });
                        }}
                      />
                    }
                    label="Scored"
                  />
                  <Typography variant="caption" color="text.secondary" display="block">
                    {form.isScored
                      ? 'Contributes to the parent score. First option must be the healthiest answer.'
                      : 'Profiling only — no right answer, contributes nothing to the score.'}
                  </Typography>
                </Grid>
              </>
            )}
          </Grid>

          {/* ---------- KG prompt picture ---------- */}
          {track === 'kg' && (
            <>
              <Divider textAlign="left">
                <Typography variant="overline">Prompt picture</Typography>
              </Divider>
              <ImageField
                value={form.promptMedia?.image || ''}
                imageKey={form.promptMedia?.imageKey}
                label="Picture shown above the options"
                helperText="Leave empty if the question needs no picture."
                onChange={(image, imageKey) =>
                  set({
                    promptMedia: image ? { image, label: form.promptMedia?.label || '', imageKey } : null,
                  })
                }
              />
              {form.promptMedia?.image && (
                <Alert severity="info" icon={<Icon icon="mdi:lightbulb-outline" />}>
                  If the answer option carries this same picture, the child can match pictures
                  without reading the word. That is usually not what the question is testing —
                  questions 1 to 3 of the live paper deliberately keep their options text-only.
                </Alert>
              )}
            </>
          )}

          {/* ---------- blanks and answers, side by side ---------- */}
          {isLadder && (
            <>
              <Divider textAlign="left">
                <Typography variant="overline">
                  Blanks and answers
                </Typography>
              </Divider>

              {form.subItems.length === 0 && (
                <Stack spacing={1}>
                  <Typography variant="body2" color="text.secondary">
                    This question has no separate blanks — one answer grades the whole question.
                  </Typography>
                  {form.correctAnswers.map((answer, index) => (
                    <AnswerField
                      key={index}
                      value={answer}
                      accepted={accepted[index]}
                      keywords={form.matchMode === 'keywords'}
                      onChange={(value) => setAnswer(index, value)}
                    />
                  ))}
                </Stack>
              )}

              {form.subItems.map((blank, index) => (
                <Stack key={index} direction="row" spacing={1} alignItems="flex-start">
                  <Typography variant="body2" sx={{ mt: 1.5, width: 20, color: 'text.disabled' }}>
                    {index + 1}
                  </Typography>
                  <TextField
                    size="small"
                    label={`Blank ${index + 1}`}
                    value={blank}
                    onChange={(e) => setBlank(index, e.target.value)}
                    placeholder="a. __ush"
                    sx={{ flex: 1 }}
                  />
                  <Box sx={{ flex: 1 }}>
                    <AnswerField
                      value={form.correctAnswers[index] || ''}
                      accepted={accepted[index]}
                      keywords={form.matchMode === 'keywords'}
                      label={`Answer ${index + 1}`}
                      onChange={(value) => setAnswer(index, value)}
                    />
                  </Box>
                  <IconButton size="small" sx={{ mt: 0.5 }} onClick={() => removeBlank(index)}>
                    <Icon icon="mdi:close" width={18} />
                  </IconButton>
                </Stack>
              ))}

              <Box>
                <Button
                  size="small"
                  startIcon={<Icon icon="mdi:plus" width={16} />}
                  onClick={addBlank}
                >
                  Add a blank
                </Button>
                <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                  Marks split evenly across the blanks
                  {form.subItems.length > 0 &&
                    ` — ${(Number(form.marks) / form.subItems.length).toFixed(2)} each`}
                </Typography>
              </Box>
            </>
          )}

          {/* ---------- options ---------- */}
          <Divider textAlign="left">
            <Typography variant="overline">Options</Typography>
          </Divider>

          {isLadder && (
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                variant={optionsLayout === null ? 'contained' : 'outlined'}
                onClick={() => switchLayout(null)}
              >
                No options
              </Button>
              <Button
                size="small"
                variant={optionsLayout === 'flat' ? 'contained' : 'outlined'}
                onClick={() => switchLayout('flat')}
              >
                One list
              </Button>
              <Button
                size="small"
                variant={optionsLayout === 'nested' ? 'contained' : 'outlined'}
                onClick={() => switchLayout('nested')}
                disabled={form.subItems.length === 0}
              >
                A list per blank
              </Button>
            </Stack>
          )}

          {isLadder && optionsLayout === null && (
            <Stack spacing={1}>
              <Typography variant="body2" color="text.secondary">
                The child types their answer. This is also how the Malayalam bank works today, where
                the choices are written into the question text itself.
              </Typography>
              {/* The Type dropdown does NOT switch this on, and that surprises
                  people. It cannot: 17 live questions are typed multiple choice
                  and carry no options at all, and 4 typed "match" DO carry them.
                  Type and layout are genuinely independent, so the editor says
                  so here rather than guessing and overwriting an author's work. */}
              {form.type === 'multiple_choice' && (
                <Alert
                  severity="info"
                  sx={{ py: 0.5 }}
                  action={
                    <Button size="small" onClick={() => switchLayout('flat')}>
                      Add a list
                    </Button>
                  }
                >
                  This is a multiple-choice question with no option list, which is allowed — it
                  means the choices are written into the question text, as the Malayalam bank
                  does. Add a list if you want the child to tap a choice instead.
                </Alert>
              )}
            </Stack>
          )}

          {isLadder && optionsLayout === 'flat' && (
            <Stack spacing={1}>
              {/* --------------------------------------------------------
                  THE ANSWER IS PICKED, NOT TYPED

                  Options used to be labels here and the answer a free-text box
                  further up, with nothing joining them. Grading is a normalised
                  exact string match, so options "Apple / Ball / Cat" with the
                  answer "apple " saved cleanly, rendered correctly, and marked
                  every child who got it right as wrong. Writing "a) Apple" as
                  the option and "Apple" as the answer did the same.

                  KG has had this radio since the beginning. Ladder papers had
                  neither it nor the check. Ticking is still optional — a `match`
                  question's answer is a pairing rather than a choice, and the
                  spaced-slash form accepts either side — so nothing is forced,
                  and the server warns rather than blocks.
                  -------------------------------------------------------- */}
              <Typography variant="body2" color="text.secondary">
                Tick the correct option to use it as the answer — that is what guarantees the
                answer is one a child can actually reach.
              </Typography>
              {form.options.map((option, index) => {
                const label = typeof option === 'string' ? option : option.label;
                const isAnswer = Boolean(label) && form.correctAnswers[0] === label;
                return (
                  <Stack key={index} direction="row" spacing={1} alignItems="center">
                    <Radio
                      size="small"
                      checked={isAnswer}
                      disabled={!label}
                      onChange={() => setAnswer(0, label)}
                    />
                    <TextField
                      size="small"
                      fullWidth
                      label={`Option ${index + 1}`}
                      value={label}
                      onChange={(e) => {
                        const next = e.target.value;
                        // Keep the tick attached to the option as it is renamed,
                        // or ticking and then correcting a typo silently unsets
                        // the answer — the same fix the KG branch already has.
                        if (isAnswer) setAnswer(0, next);
                        setOption(index, { label: next });
                      }}
                    />
                    <IconButton size="small" onClick={() => removeOption(index)}>
                      <Icon icon="mdi:close" width={18} />
                    </IconButton>
                  </Stack>
                );
              })}
              <Box>
                <Button size="small" startIcon={<Icon icon="mdi:plus" width={16} />} onClick={addFlatOption}>
                  Add an option
                </Button>
              </Box>
            </Stack>
          )}

          {isLadder && optionsLayout === 'nested' && (
            <Stack spacing={2}>
              <Alert severity="info" sx={{ py: 0.5 }}>
                Each blank gets its own list of choices. Every blank needs one, or that blank
                silently shows the first blank&apos;s options.
              </Alert>
              {form.subItems.map((blank, groupIndex) => (
                <Box key={groupIndex} sx={{ pl: 1, borderLeft: '2px solid', borderColor: 'divider' }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Blank {groupIndex + 1} {blank ? `— ${blank}` : ''}
                  </Typography>
                  <Stack spacing={1}>
                    {(form.options[groupIndex] || []).map((option, optionIndex) => {
                      // Same picker as the flat list, per blank: blank 2's radio
                      // sets answer 2. Without it an author has to retype each
                      // choice into the answer box above, character for
                      // character, and a stray space marks the child wrong.
                      const isAnswer =
                        Boolean(option) && form.correctAnswers[groupIndex] === option;
                      return (
                        <Stack key={optionIndex} direction="row" spacing={1} alignItems="center">
                          <Radio
                            size="small"
                            checked={isAnswer}
                            disabled={!option}
                            onChange={() => setAnswer(groupIndex, option)}
                          />
                          <TextField
                            size="small"
                            fullWidth
                            value={option}
                            onChange={(e) => {
                              const next = e.target.value;
                              if (isAnswer) setAnswer(groupIndex, next);
                              setNestedOption(groupIndex, optionIndex, next);
                            }}
                          />
                          <IconButton
                            size="small"
                            onClick={() => removeNestedOption(groupIndex, optionIndex)}
                          >
                            <Icon icon="mdi:close" width={18} />
                          </IconButton>
                        </Stack>
                      );
                    })}
                    <Box>
                      <Button size="small" onClick={() => addNestedOption(groupIndex)}>
                        Add an option
                      </Button>
                    </Box>
                  </Stack>
                </Box>
              ))}
            </Stack>
          )}

          {/* KG — the correct answer is PICKED, never typed. */}
          {track === 'kg' && (
            <Stack spacing={1.5}>
              <Typography variant="body2" color="text.secondary">
                Tick the correct option. The child taps a picture, so the answer has to be one of
                these — picking it here is what guarantees it is reachable.
              </Typography>
              {form.options.map((option, index) => (
                <Stack
                  key={index}
                  direction="row"
                  spacing={1}
                  alignItems="flex-start"
                  sx={{
                    p: 1,
                    borderRadius: 1,
                    bgcolor:
                      form.correctAnswers[0] && form.correctAnswers[0] === option.label
                        ? 'success.lighter'
                        : 'transparent',
                  }}
                >
                  <Radio
                    size="small"
                    sx={{ mt: 1 }}
                    checked={Boolean(option.label) && form.correctAnswers[0] === option.label}
                    onChange={() => set({ correctAnswers: [option.label] })}
                    disabled={!option.label}
                  />
                  <Box sx={{ flex: 1 }}>
                    <ImageField
                      size={44}
                      value={option.image || ''}
                      imageKey={option.imageKey}
                      label={`Option ${index + 1} picture`}
                      onChange={(image, imageKey) => setOption(index, { image, imageKey })}
                    />
                    <TextField
                      size="small"
                      fullWidth
                      sx={{ mt: 1 }}
                      label={`Option ${index + 1} label`}
                      value={option.label}
                      onChange={(e) => {
                        const label = e.target.value;
                        // Keep the selection attached to the option as it is
                        // renamed, or ticking then editing silently unsets the
                        // answer.
                        const wasCorrect = form.correctAnswers[0] === option.label;
                        setOption(index, { label });
                        if (wasCorrect) set({ correctAnswers: [label] });
                      }}
                    />
                  </Box>
                  <IconButton size="small" onClick={() => removeOption(index)}>
                    <Icon icon="mdi:close" width={18} />
                  </IconButton>
                </Stack>
              ))}
              <Box>
                <Button size="small" startIcon={<Icon icon="mdi:plus" width={16} />} onClick={addFlatOption}>
                  Add an option
                </Button>
              </Box>
            </Stack>
          )}

          {/* Parent — scores, highest first. */}
          {track === 'parent' && (
            <Stack spacing={1.5}>
              {form.isScored && (
                <Alert
                  severity="info"
                  action={
                    <Button size="small" onClick={applyDescendingScores}>
                      Fix order
                    </Button>
                  }
                >
                  The first option must be the healthiest answer, scoring highest. A high total has
                  to mean a thriving child.
                </Alert>
              )}
              {form.options.map((option, index) => (
                <Stack key={index} direction="row" spacing={1} alignItems="center">
                  <Typography variant="body2" sx={{ width: 20, color: 'text.disabled' }}>
                    {index + 1}
                  </Typography>
                  <TextField
                    size="small"
                    fullWidth
                    label={`Option ${index + 1}`}
                    value={option.label}
                    onChange={(e) => setOption(index, { label: e.target.value })}
                  />
                  {form.isScored && (
                    <TextField
                      size="small"
                      type="number"
                      label="Score"
                      sx={{ width: 100 }}
                      value={option.score ?? ''}
                      onChange={(e) =>
                        setOption(index, {
                          score: e.target.value === '' ? null : Number(e.target.value),
                        })
                      }
                    />
                  )}
                  <IconButton size="small" onClick={() => removeOption(index)}>
                    <Icon icon="mdi:close" width={18} />
                  </IconButton>
                </Stack>
              ))}
              <Box>
                <Button size="small" startIcon={<Icon icon="mdi:plus" width={16} />} onClick={addFlatOption}>
                  Add an option
                </Button>
              </Box>
            </Stack>
          )}

          <FormControlLabel
            control={
              <Checkbox
                checked={form.isEnabled}
                onChange={(e) => set({ isEnabled: e.target.checked })}
              />
            }
            label="Show this question in the assessment"
          />
        </Stack>
      </DialogContent>

      <DialogActions>
        {validation.checking && <CircularProgress size={16} sx={{ mr: 1 }} />}
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || blockingErrors.length > 0}
        >
          {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Add question'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/**
 * One answer, with a live preview of what the grader will accept.
 *
 * The preview is the whole point. " / " means "either of these" and a bare "/"
 * is part of the answer, which is a rule nobody remembers and which produces an
 * unanswerable question when forgotten. Showing the result of the rule needs no
 * remembering.
 */
const AnswerField = ({ value, onChange, accepted, keywords, label = 'Correct answer' }) => (
  <Stack spacing={0.5}>
    <TextField
      size="small"
      fullWidth
      label={label}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={keywords ? 'flat/2d, solid/3d' : 'Brush'}
    />
    {accepted?.mode === 'exact' && accepted.accepts?.length > 0 && (
      <Stack direction="row" spacing={0.5} flexWrap="wrap" alignItems="center">
        <Typography variant="caption" color="text.secondary">
          Accepts:
        </Typography>
        {accepted.accepts.map((option) => (
          <Chip key={option} size="small" variant="outlined" label={option} sx={{ height: 20 }} />
        ))}
      </Stack>
    )}
    {accepted?.mode === 'keywords' && (
      <Typography variant="caption" color="text.secondary">
        Needs all of:{' '}
        {accepted.requires?.map((group, i) => (
          <span key={i}>
            {i > 0 && ' + '}
            <strong>{group.join(' or ')}</strong>
          </span>
        ))}
      </Typography>
    )}
  </Stack>
);

export default QuestionEditorDialog;
